import React, { useState, useEffect } from "react";
import axios from "axios";
import { Icon } from "../Icon";

interface IntegrationsSettingsProps {
  user: {
    id: string;
    username: string;
    userType: "user" | "premium" | "vip" | "admin";
  };
  authToken: string;
}

interface RaidRunnerStatus {
  connected: boolean;
  connectedAt?: string;
  hasActiveEvent?: boolean;
}

interface PatreonStatus {
  connected: boolean;
  enabled?: boolean;
  patreonUserId?: string;
  connectedAt?: string;
}

const IntegrationsSettings: React.FC<IntegrationsSettingsProps> = ({
  authToken,
}) => {
  const [raidRunnerStatus, setRaidRunnerStatus] = useState<RaidRunnerStatus>({
    connected: false,
  });
  const [raidRunnerLoading, setRaidRunnerLoading] = useState(false);
  const [raidRunnerApiSecret, setRaidRunnerApiSecret] = useState("");
  const [raidRunnerError, setRaidRunnerError] = useState<string | null>(null);
  const [patreonStatus, setPatreonStatus] = useState<PatreonStatus>({
    connected: false,
  });
  const [patreonLoading, setPatreonLoading] = useState(false);
  const [streamScannerStatus, setStreamScannerStatus] = useState<{
    connected: boolean;
    connectedAt?: string | null;
  }>({ connected: false });
  const [streamScannerLoading, setStreamScannerLoading] = useState(false);

  useEffect(() => {
    // Patreon is free for everyone now — fetch its status for all users.
    fetchPatreonStatus();
    fetchRaidRunnerStatus();
    fetchStreamScannerStatus();

    // Check for callback status
    const urlParams = new URLSearchParams(window.location.search);
    const ssResult = urlParams.get("streamscanner");
    if (ssResult) {
      if (ssResult === "connected") fetchStreamScannerStatus();
      else if (ssResult === "denied")
        alert("StreamScanner authorization was cancelled.");
      else if (ssResult === "error")
        alert("Failed to connect StreamScanner. Please try again.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    const patreonStatus = urlParams.get("patreon");
    const error = urlParams.get("error");

    if (patreonStatus === "connected") {
      // Already connected via the callback, just clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Handle Patreon OAuth callback
    const patreonCode = urlParams.get("code");
    const state = urlParams.get("state");
    if (
      patreonCode &&
      (state === undefined || state === "None" || state === null)
    ) {
      // This is likely a Patreon callback
      handlePatreonCallback(patreonCode);
    }

    if (error === "patreon_auth_failed") {
      setTimeout(() => {
        alert("Failed to connect to Patreon. Please try again.");
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }, 100);
    }
  }, [authToken]);

  const fetchRaidRunnerStatus = async () => {
    try {
      const response = await axios.get("/api/raidrunner/status", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setRaidRunnerStatus(response.data);
    } catch (error) {
      console.error("Error fetching RaidRunner status:", error);
    }
  };

  const fetchPatreonStatus = async () => {
    try {
      const response = await axios.get("/api/patreon/status", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setPatreonStatus(response.data);
    } catch (error) {
      console.error("Error fetching Patreon status:", error);
    }
  };

  const handleRaidRunnerConnect = async () => {
    if (!raidRunnerApiSecret.trim()) {
      setRaidRunnerError("Please enter your RaidRunner API secret");
      return;
    }

    setRaidRunnerLoading(true);
    setRaidRunnerError(null);
    try {
      const response = await axios.post(
        "/api/raidrunner/connect",
        { apiSecret: raidRunnerApiSecret },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      if (response.data.success) {
        setRaidRunnerStatus({
          connected: true,
          connectedAt: new Date().toISOString(),
          hasActiveEvent: response.data.hasActiveEvent,
        });
        setRaidRunnerApiSecret("");
        alert("Successfully connected to RaidRunner!");
      } else {
        setRaidRunnerError(response.data.message || "Invalid API secret");
      }
    } catch (error) {
      console.error("Error connecting RaidRunner:", error);
      setRaidRunnerError("Invalid API secret. Please check and try again.");
    } finally {
      setRaidRunnerLoading(false);
    }
  };

  const handleRaidRunnerDisconnect = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect your RaidRunner integration?",
      )
    ) {
      return;
    }

    setRaidRunnerLoading(true);
    try {
      await axios.delete("/api/raidrunner/disconnect", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setRaidRunnerStatus({ connected: false });
      setRaidRunnerApiSecret("");
    } catch (error) {
      console.error("Error disconnecting RaidRunner:", error);
      alert("Failed to disconnect RaidRunner. Please try again.");
    } finally {
      setRaidRunnerLoading(false);
    }
  };

  const handlePatreonConnect = async () => {
    setPatreonLoading(true);
    try {
      const response = await axios.get("/api/patreon/auth", {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        alert("Failed to get Patreon authorization URL. Please try again.");
      }
    } catch (error: any) {
      console.error("Error initiating Patreon auth:", error);
      alert("Failed to connect to Patreon. Please try again.");
    } finally {
      setPatreonLoading(false);
    }
  };

  const fetchStreamScannerStatus = async () => {
    try {
      const response = await axios.get("/api/streamscanner/status", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setStreamScannerStatus(response.data);
    } catch (error) {
      console.error("Error fetching StreamScanner status:", error);
    }
  };

  const handleStreamScannerConnect = async () => {
    setStreamScannerLoading(true);
    try {
      const response = await axios.get("/api/streamscanner/connect", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        alert("Failed to start StreamScanner connection. Please try again.");
        setStreamScannerLoading(false);
      }
    } catch (error) {
      console.error("Error initiating StreamScanner auth:", error);
      alert("Failed to connect StreamScanner. Please try again.");
      setStreamScannerLoading(false);
    }
  };

  const handleStreamScannerDisconnect = async () => {
    if (!window.confirm("Disconnect your StreamScanner integration?")) return;
    setStreamScannerLoading(true);
    try {
      await axios.delete("/api/streamscanner/disconnect", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setStreamScannerStatus({ connected: false });
    } catch (error) {
      console.error("Error disconnecting StreamScanner:", error);
      alert("Failed to disconnect StreamScanner. Please try again.");
    } finally {
      setStreamScannerLoading(false);
    }
  };

  const handlePatreonDisconnect = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect your Patreon account?",
      )
    ) {
      return;
    }

    setPatreonLoading(true);
    try {
      await axios.delete("/api/patreon/disconnect", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setPatreonStatus({ connected: false });
    } catch (error) {
      console.error("Error disconnecting Patreon:", error);
      alert("Failed to disconnect Patreon. Please try again.");
    } finally {
      setPatreonLoading(false);
    }
  };

  const handlePatreonToggle = async (enabled: boolean) => {
    setPatreonLoading(true);
    try {
      await axios.patch(
        "/api/patreon/toggle",
        { enabled },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      setPatreonStatus({ ...patreonStatus, enabled });
    } catch (error) {
      console.error("Error toggling Patreon:", error);
      alert("Failed to update Patreon settings. Please try again.");
    } finally {
      setPatreonLoading(false);
    }
  };

  const handlePatreonCallback = async (code: string) => {
    try {
      const response = await axios.get(`/api/patreon/callback?code=${code}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data.success) {
        // Show success message and refresh the page
        alert("Successfully connected to Patreon!");
        window.location.href = window.location.pathname;
      } else {
        window.location.href =
          window.location.pathname + "?error=patreon_auth_failed";
      }
    } catch (error) {
      console.error("Error handling Patreon callback:", error);
      window.location.href =
        window.location.pathname + "?error=patreon_auth_failed";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div style={{ maxWidth: "800px" }}>
      <h3 style={{ fontSize: "20px", margin: "0 0 24px 0", color: "#e2e8f0" }}>
        Integrations
      </h3>

      {/* RaidRunner Integration - Available for All Users */}
      <div
        style={{
          backgroundColor: "#2d3748",
          padding: "24px",
          borderRadius: "8px",
          marginBottom: "20px",
          border: "1px solid #4a5568",
        }}
      >
        <h4
          style={{
            margin: "0 0 16px 0",
            color: "#FF6B6B",
            fontSize: "18px",
          }}
        >
          <Icon
            name="train"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          RaidRunner Integration
        </h4>

        {!raidRunnerStatus.connected ? (
          <>
            <p
              style={{
                color: "#cbd5e0",
                marginBottom: "20px",
                lineHeight: "1.6",
              }}
            >
              Connect to RaidRunner to track raid train events. HAL will be able
              to see current raid train information during events.
            </p>

            <div style={{ marginBottom: "16px" }}>
              <input
                type="password"
                placeholder="Enter your RaidRunner API secret"
                value={raidRunnerApiSecret}
                onChange={(e) => {
                  setRaidRunnerApiSecret(e.target.value);
                  setRaidRunnerError(null);
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleRaidRunnerConnect();
                  }
                }}
                disabled={raidRunnerLoading}
                style={{
                  width: "100%",
                  padding: "12px",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  border: raidRunnerError
                    ? "1px solid #EF4444"
                    : "1px solid #4a5568",
                  borderRadius: "6px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "border-color 0.2s",
                  boxSizing: "border-box",
                }}
              />
              {raidRunnerError && (
                <p
                  style={{
                    color: "#EF4444",
                    marginTop: "8px",
                    fontSize: "14px",
                  }}
                >
                  {raidRunnerError}
                </p>
              )}
            </div>

            <button
              onClick={handleRaidRunnerConnect}
              disabled={raidRunnerLoading}
              style={{
                width: "100%",
                padding: "14px 24px",
                backgroundColor: raidRunnerLoading ? "#4a5568" : "#FF6B6B",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: raidRunnerLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!raidRunnerLoading) {
                  e.currentTarget.style.backgroundColor = "#E53E3E";
                }
              }}
              onMouseLeave={(e) => {
                if (!raidRunnerLoading) {
                  e.currentTarget.style.backgroundColor = "#FF6B6B";
                }
              }}
            >
              {raidRunnerLoading ? "Connecting..." : "Connect RaidRunner"}
            </button>

            <p
              style={{
                color: "#a0aec0",
                marginTop: "12px",
                fontSize: "14px",
                lineHeight: "1.6",
              }}
            >
              Get your API secret from{" "}
              <a
                href="https://raidrunner.net"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#FF6B6B" }}
              >
                RaidRunner.net
              </a>
            </p>
          </>
        ) : (
          <>
            <div
              style={{
                backgroundColor: "#1a202c",
                padding: "16px",
                borderRadius: "6px",
                marginBottom: "20px",
              }}
            >
              <p
                style={{
                  color: "#48BB78",
                  margin: "0 0 8px 0",
                  fontWeight: "bold",
                }}
              >
                <Icon
                  name="check"
                  size={16}
                  color="#48BB78"
                  style={{ verticalAlign: "-3px", marginRight: "8px" }}
                />
                Connected to RaidRunner
              </p>
              <p style={{ color: "#a0aec0", margin: 0, fontSize: "14px" }}>
                Connected{" "}
                {new Date(raidRunnerStatus.connectedAt!).toLocaleDateString()}
              </p>
              {raidRunnerStatus.hasActiveEvent && (
                <p
                  style={{
                    color: "#F6E05E",
                    margin: "8px 0 0 0",
                    fontSize: "14px",
                  }}
                >
                  <Icon
                    name="smile"
                    size={16}
                    style={{ verticalAlign: "-3px", marginRight: "8px" }}
                  />
                  Active raid train event detected
                </p>
              )}
            </div>

            <button
              onClick={handleRaidRunnerDisconnect}
              disabled={raidRunnerLoading}
              style={{
                padding: "10px 20px",
                backgroundColor: "transparent",
                color: "#EF4444",
                border: "1px solid #EF4444",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: raidRunnerLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!raidRunnerLoading) {
                  e.currentTarget.style.backgroundColor = "#EF4444";
                  e.currentTarget.style.color = "white";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#EF4444";
              }}
            >
              {raidRunnerLoading ? "Processing..." : "Disconnect RaidRunner"}
            </button>
          </>
        )}
      </div>

      {/* Patreon Integration - free for everyone */}
      <div
        style={{
          backgroundColor: "#2d3748",
          padding: "24px",
          borderRadius: "8px",
          marginBottom: "20px",
          border: "1px solid #4a5568",
        }}
      >
        <h4
          style={{
            margin: "0 0 16px 0",
            color: "#FF424D",
            fontSize: "18px",
          }}
        >
          <Icon
            name="palette"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Patreon Integration
        </h4>

        {
          // Patreon — available to everyone
          <>
            {!patreonStatus.connected ? (
              <>
                <p
                  style={{
                    color: "#cbd5e0",
                    marginBottom: "20px",
                    lineHeight: "1.6",
                  }}
                >
                  Connect your Patreon account to acknowledge your supporters in
                  real-time. HAL will see new subscribers and renewals during
                  your stream and can thank them in chat. The most recent 10
                  Patreon events will be included in HAL&apos;s context during
                  streams.
                </p>

                <button
                  onClick={handlePatreonConnect}
                  disabled={patreonLoading}
                  style={{
                    padding: "12px 24px",
                    backgroundColor: patreonLoading ? "#4a5568" : "#FF424D",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    cursor: patreonLoading ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "center",
                    margin: "0 auto",
                  }}
                >
                  {patreonLoading ? "Loading..." : "Connect to Patreon"}
                </button>
              </>
            ) : (
              <>
                <div
                  style={{
                    backgroundColor: "#1a202c",
                    padding: "16px",
                    borderRadius: "6px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        backgroundColor: "#FF424D",
                        borderRadius: "50%",
                      }}
                    />
                    <p
                      style={{
                        margin: 0,
                        color: "#FF424D",
                        fontWeight: "bold",
                      }}
                    >
                      Connected to Patreon
                    </p>
                  </div>
                  {patreonStatus.patreonUserId && (
                    <p
                      style={{
                        margin: "4px 0",
                        color: "#a0aec0",
                        fontSize: "14px",
                      }}
                    >
                      Account ID: {patreonStatus.patreonUserId}
                    </p>
                  )}
                  {patreonStatus.connectedAt && (
                    <p
                      style={{
                        margin: "4px 0",
                        color: "#a0aec0",
                        fontSize: "14px",
                      }}
                    >
                      Connected: {formatDate(patreonStatus.connectedAt)}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={patreonStatus.enabled}
                      onChange={(e) => handlePatreonToggle(e.target.checked)}
                      disabled={patreonLoading}
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: patreonLoading ? "not-allowed" : "pointer",
                      }}
                    />
                    <span style={{ color: "#cbd5e0", fontSize: "16px" }}>
                      Show Patreon supporters in chat context
                    </span>
                  </label>
                  <p
                    style={{
                      marginTop: "8px",
                      marginLeft: "32px",
                      color: "#a0aec0",
                      fontSize: "14px",
                    }}
                  >
                    When enabled, HAL will see recent Patreon subscribers during
                    your stream and can acknowledge them.
                  </p>
                </div>

                <button
                  onClick={handlePatreonDisconnect}
                  disabled={patreonLoading}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "transparent",
                    color: "#EF4444",
                    border: "1px solid #EF4444",
                    borderRadius: "6px",
                    fontSize: "14px",
                    cursor: patreonLoading ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (!patreonLoading) {
                      e.currentTarget.style.backgroundColor = "#EF4444";
                      e.currentTarget.style.color = "white";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#EF4444";
                  }}
                >
                  {patreonLoading ? "Processing..." : "Disconnect Patreon"}
                </button>
              </>
            )}
          </>
        }
      </div>

      {/* StreamScanner — one-click OAuth */}
      <div
        style={{
          backgroundColor: "#1a1a2e",
          border: "1px solid #2a2a4a",
          borderRadius: "12px",
          padding: "24px",
          marginTop: "20px",
        }}
      >
        <h3 style={{ color: "#e2e8f0", marginTop: 0, marginBottom: "8px" }}>
          StreamScanner
        </h3>
        <p style={{ color: "#a0aec0", fontSize: "14px", marginTop: 0 }}>
          Let HAL see your recent stream events — subs, cheers, raids, gift subs
          and donations — so it can react to who&apos;s supporting your stream
          in real time.
        </p>
        {streamScannerStatus.connected ? (
          <>
            <p style={{ color: "#10B981", fontSize: "14px" }}>
              <Icon
                name="check"
                size={16}
                color="#10B981"
                style={{ verticalAlign: "-3px", marginRight: "8px" }}
              />
              Connected
              {streamScannerStatus.connectedAt
                ? ` since ${new Date(streamScannerStatus.connectedAt).toLocaleDateString()}`
                : ""}
            </p>
            <button
              onClick={handleStreamScannerDisconnect}
              disabled={streamScannerLoading}
              style={{
                padding: "10px 20px",
                backgroundColor: "transparent",
                color: "#EF4444",
                border: "1px solid #EF4444",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: streamScannerLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!streamScannerLoading) {
                  e.currentTarget.style.backgroundColor = "#EF4444";
                  e.currentTarget.style.color = "white";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "#EF4444";
              }}
            >
              {streamScannerLoading
                ? "Processing..."
                : "Disconnect StreamScanner"}
            </button>
          </>
        ) : (
          <button
            onClick={handleStreamScannerConnect}
            disabled={streamScannerLoading}
            style={{
              backgroundColor: "#9146FF",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              cursor: streamScannerLoading ? "not-allowed" : "pointer",
              fontWeight: "bold",
            }}
          >
            {streamScannerLoading ? "Connecting..." : "Connect StreamScanner"}
          </button>
        )}
      </div>
    </div>
  );
};

export default IntegrationsSettings;
